import { useState } from "react";
import { Table, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";

import sdk from "@/sdk";
import { CorrectionType } from "@/graphql";
import { formatBalance } from "@/utils/formatters";

const CORRECTION_TYPE_LABELS: Record<string, string> = {
  [CorrectionType.CashWithdrawal]: "Бэлэн мөнгө",
  [CorrectionType.HoldWithdrawal]: "Худалдан авалтаас үлдсэн дүн",
  [CorrectionType.BondInterestRecalc]: "Бондын хүү",
  [CorrectionType.StockAdjustment]: "Хувьцаа",
  STOCK_AMOUNT_ADJUSTMENT: "Хувьцаа тоо ширхэг",
  STOCK_PRICE_ADJUSTMENT: "Хувьцаа ханш",
};

const CORRECTION_TYPE_COLORS: Record<string, string> = {
  [CorrectionType.CashWithdrawal]: "blue",
  [CorrectionType.HoldWithdrawal]: "orange",
  [CorrectionType.BondInterestRecalc]: "green",
  [CorrectionType.StockAdjustment]: "purple",
  STOCK_AMOUNT_ADJUSTMENT: "purple",
  STOCK_PRICE_ADJUSTMENT: "magenta",
};

interface CorrectionHistoryProps {
  userId: string;
}

export function CorrectionHistory({ userId }: CorrectionHistoryProps) {
  const [page, setPage] = useState(0);
  const limit = 10;

  const { data, isLoading } = useQuery({
    queryKey: ["correctionOrders", userId, page],
    queryFn: () =>
      sdk.GetCorrectionOrders({
        page,
        limit,
        filter: { userId },
      }),
    select: (data) => data.getCorrectionOrders,
    keepPreviousData: true,
  });

  return (
    <Table
      loading={isLoading}
      dataSource={data?.orders ?? []}
      rowKey="_id"
      size="small"
      bordered
      pagination={{
        current: page + 1,
        pageSize: limit,
        total: (data?.totalPages ?? 1) * limit,
        onChange: (p) => setPage(p - 1),
        showSizeChanger: false,
      }}
      columns={[
        {
          title: "Огноо",
          dataIndex: "createdAt",
          width: 160,
          render: (v: number) =>
            new Date(v).toISOString().replace("T", " ").slice(0, 19),
        },
        {
          title: "Төрөл",
          dataIndex: "correctionType",
          width: 140,
          render: (v: string) => (
            <Tag color={CORRECTION_TYPE_COLORS[v] ?? "default"}>
              {CORRECTION_TYPE_LABELS[v] ?? v}
            </Tag>
          ),
        },
        {
          title: "Дүн",
          dataIndex: "amount",
          width: 140,
          render: (v: number) => <span>{formatBalance({ balance: v })} ₮</span>,
        },
        {
          title: "Хуучин дүн",
          dataIndex: "oldValue",
          width: 140,
          render: (v: number | null) =>
            v != null ? <span>{formatBalance({ balance: v })} ₮</span> : "—",
        },
        {
          title: "Шинэ дүн",
          dataIndex: "newValue",
          width: 140,
          render: (v: number | null) =>
            v != null ? <span>{formatBalance({ balance: v })} ₮</span> : "—",
        },
        {
          title: "Тайлбар",
          dataIndex: "description",
        },
        {
          title: "Шалтгаан",
          dataIndex: "reason",
          ellipsis: true,
        },
      ]}
    />
  );
}
